// Считает стоимость доставки для одной позиции кирпича (изолированно, как будто
// она единственная в машине), переиспользуя серверный расчёт /api/delivery_gaz
// (тот же, что использует сравнение производств для газобетона и кирпича —
// BackendProductions.js/calculateDelivery.js, категория "gaz").
//
// Сервер проверяет productionInfo только на истинность (routes/deliveryGazCalculator.js) —
// регион/координаты производителя он сам находит по имени material.manufacturer,
// поэтому доп. запрос вроде /api/filter не нужен, объект material собирается
// напрямую из options[name] (те же сырые поля, что использует createDeliveryMaterial
// в BackendProductions.js).

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

const GAZ_FIELD_ALIASES = {
  UNITS_PER_PALLET: ["number_per_pallet", "NUMBER_PER_PALLET", "UNITS_PER_PALLET"],
  PALLET_UNITS: ["PALLET_UNITS", "pallet_per_unit"],
  SHALANDA_PALLETS: ["SHALANDA_PALLETS", "scow_quantitiy"],
  MANIPULATOR_5T: ["MANIPULATOR_5T", "manipulator_5t"],
  MANIPULATOR_10T: ["MANIPULATOR_10T", "manipulator_10t"],
};

const parsePositiveNumber = (value) => {
  const parsed = Number.parseFloat(String(value ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getAliasedPositiveValue = (row, fieldName) => {
  const aliases = GAZ_FIELD_ALIASES[fieldName] || [fieldName];
  for (const alias of aliases) {
    const value = parsePositiveNumber(row?.[alias]);
    if (value !== null) return value;
  }
  return null;
};

// Кирпич считается поштучно (не входит в категории с округлением до поддона/фуры —
// см. src/utils/gazPalletRules.js), поэтому quantity передаётся как есть, без
// доп. округления, в отличие от части позиций газобетона.
export const buildGazDeliveryMaterial = (name, data, quantity) => {
  const palletUnits = getAliasedPositiveValue(data, "UNITS_PER_PALLET")
    || getAliasedPositiveValue(data, "PALLET_UNITS")
    || 1;

  return {
    calculator: data.CALCULATOR || "Нет данных",
    category: data.CATEGORY || "Нет данных",
    // options ключуется по номенклатуре — это и есть искомое имя для поиска нормы
    // погрузки на сервере (resolveTransportCapacities), надёжнее, чем data.NOMENCLATURE,
    // которое может быть не заполнено на части строк прайса.
    name: name || data.NOMENCLATURE || "",
    quantity,
    unit: data.BASE_UNIT || "шт",
    massPerOne: parsePositiveNumber(data.WEIGHT_TONS) || 0,
    volumePerOne: parsePositiveNumber(data.VOLUME_CUBIC) || 1,
    manufacturer: data.MANUFACTURER || "",
    numberPallets: Math.ceil(quantity / palletUnits),
    palletUnits,
    shalandaCapacity: getAliasedPositiveValue(data, "SHALANDA_PALLETS"),
    manipulator5tCapacity: getAliasedPositiveValue(data, "MANIPULATOR_5T"),
    manipulatorCapacity: getAliasedPositiveValue(data, "MANIPULATOR_10T"),
    bitrixId: data.BITRIX_ID || "",
    source: data.SOURCE || "-",
    region: data.REGION || null,
    guideId: data.GUIDE_ID || null,
  };
};

// Возвращает { totalCost } или { error: true } — никогда не бросает исключение,
// чтобы сбой доставки не ронял список аналогов.
export const fetchBrickDeliveryCost = async ({
  material,
  addressAndCoordinates,
  selectedRegion,
  deliveryMethod,
  paymentMethod,
  signal,
}) => {
  if (!material?.manufacturer) {
    return { error: true };
  }

  try {
    const response = await fetch(`${PATH_TO_BACKEND}/api/delivery_gaz`, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        materials: [material],
        deliveryMethod: deliveryMethod?.gaz || "Оптимальный маршрут",
        unloadMethod: deliveryMethod?.gazUnloadMethod || "без разгрузки",
        addressAndCoordinates: { ...addressAndCoordinates, region: selectedRegion },
        productionInfo: { NAME: material.manufacturer },
        paymentMethod: paymentMethod || "cash",
      }),
    });

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null);
      console.error("❌ /api/delivery_gaz отклонил запрос доставки аналога кирпича:", errorPayload?.error || response.status);
      return { error: true };
    }

    const payload = await response.json();
    const totalCost = Number(payload?.data?.totalCost);
    return Number.isFinite(totalCost) ? { totalCost } : { error: true };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }
    console.error("❌ Ошибка при расчёте доставки аналога кирпича:", error);
    return { error: true };
  }
};
